import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { signOut } from 'aws-amplify/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import AuthWrapper from './components/AuthWrapper';
import Navigation from './components/Navigation';
import PortfolioDashboard from './components/PortfolioDashboard';
import QuickProfitMonitor from './components/QuickProfitMonitor';
import TradeGroups from './components/TradeGroups';
import TradeGroupDetail from './components/TradeGroupDetail';
import CapitalDeployment from './components/CapitalDeployment';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import TradingConfiguration from './components/TradingConfiguration';
import ApplicationSettings from './components/ApplicationSettings';
import { TaxReportingSection } from './components/taxReporting/TaxReportingSection';
import MarketOverviewDashboard from './components/MarketOverviewDashboard';
import ChartView from './components/ChartView';
import { GlobalAutoRefreshToggle } from './components/shared/GlobalAutoRefreshToggle';
import { startGlobalAutoRefreshInterval } from './stores/useGlobalAutoRefreshStore';
import authConfig from './aws-config';
import './App.css';

// Configure Amplify with Cognito settings
Amplify.configure(authConfig);

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  },
});

// Hamburger menu icon component - now using clean SVG
const MenuToggleIcon: React.FC<{ isCollapsed: boolean }> = ({ }) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

function App() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Initialize global auto-refresh interval on mount
  useEffect(() => {
    const cleanup = startGlobalAutoRefreshInterval();
    // Cleanup interval on unmount
    return cleanup;
  }, []);


  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.log('Error signing out: ', error);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleNavigation = () => {
    // On mobile, close the sidebar after navigation
    if (window.innerWidth <= 768) {
      setIsSidebarCollapsed(true);
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthWrapper>
        <Router>
          <div className={`App ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <header className="App-header">
              <div className="header-top">
                <div className="header-left">
                  <button
                    className="menu-toggle-button"
                    onClick={toggleSidebar}
                    title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    aria-label="Toggle sidebar"
                  >
                    <MenuToggleIcon isCollapsed={isSidebarCollapsed} />
                  </button>
                  <div className="environment-badge">
                    {import.meta.env.VITE_ENVIRONMENT || 'development'}
                  </div>
                  <div className="system-status">
                    <span className="status-indicator">●</span>
                    <span>System Online</span>
                  </div>
                </div>
                <div className="header-right">
                  <div className="user-info">
                    <GlobalAutoRefreshToggle />
                    <button
                      onClick={handleSignOut}
                      className="logout-button"
                      type="button"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </header>

            <Navigation isCollapsed={isSidebarCollapsed} onNavigate={handleNavigation} />

            <main className="main-content">
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<PortfolioDashboard />} />
                <Route path="/profit" element={<QuickProfitMonitor />} />
                <Route path="/trade-groups" element={<TradeGroups />} />
                <Route path="/trade-groups/:tradeGroupId" element={<TradeGroupDetail />} />
                <Route path="/capital" element={<CapitalDeployment />} />
                <Route path="/analytics" element={<PerformanceAnalytics />} />
                <Route path="/market-overview" element={<MarketOverviewDashboard />} />
                <Route path="/chart/:productId" element={<ChartView />} />
                <Route path="/market/:productId" element={<ChartView />} />
                <Route path="/trading-config" element={<TradingConfiguration />} />
                <Route path="/tax-reporting/*" element={<TaxReportingSection />} />
                <Route path="/settings" element={<ApplicationSettings />} />
              </Routes>
            </main>

            <footer className="app-footer">
              <div className="footer-content">
                <p className="footer-main">Cooter Capital - Crypto Trading Platform - v1.48.0-SNAPSHOT</p>
                <p className="footer-slogan">Where dreams are made...<span className="footer-lips">�</span></p>
                <p className="footer-meta">© 2025 • Status: Online • Last Updated: {new Date().toLocaleDateString()}</p>
              </div>
            </footer>
          </div>
        </Router>
      </AuthWrapper>
    </QueryClientProvider>
  );
}

export default App;
